import React, { useState } from 'react'
import { X, FileText, Type, UploadCloud } from 'lucide-react'

// Pannello per inserire manualmente un ordine non rilevato automaticamente
// (o arrivato da un canale diverso dalla mail). L'utente sceglie tra
// caricare un PDF o incollare il testo grezzo di una mail.
//
// onCreate riceve { mode, file, text } — la creazione vera e propria
// dell'ordine (con relativo job lato backend) è gestita da chi usa questo
// componente (vedi Dashboard.jsx), che oggi crea solo un ordine locale
// finché non esiste un endpoint reale per l'inserimento manuale.
export default function NewOrderModal({ onClose, onCreate }) {
  const [mode, setMode] = useState('pdf')
  const [file, setFile] = useState(null)
  const [text, setText] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const canSubmit = mode === 'pdf' ? Boolean(file) : text.trim().length > 0

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    if (f) setFile(f)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f && f.type === 'application/pdf') setFile(f)
  }

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return
    setSubmitting(true)
    try {
      await onCreate({ mode, file, text: text.trim() })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h3 style={s.title}>Nuovo ordine</h3>
          <button style={s.closeBtn} onClick={onClose} aria-label="Chiudi">
            <X size={18} />
          </button>
        </div>
        <p style={s.subtitle}>
          Usa questa opzione per un ordine non rilevato automaticamente, o arrivato da un canale diverso dalla mail.
        </p>

        <div style={s.tabs}>
          <button
            style={{ ...s.tab, ...(mode === 'pdf' ? s.tabActive : {}) }}
            onClick={() => setMode('pdf')}
          >
            <FileText size={14} /> Inserisci PDF
          </button>
          <button
            style={{ ...s.tab, ...(mode === 'text' ? s.tabActive : {}) }}
            onClick={() => setMode('text')}
          >
            <Type size={14} /> Incolla testo mail
          </button>
        </div>

        {mode === 'pdf' ? (
          <label
            style={{ ...s.dropzone, ...(dragOver ? s.dropzoneActive : {}) }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <UploadCloud size={26} color="var(--text-3)" />
            {file ? (
              <span style={s.fileName}>{file.name}</span>
            ) : (
              <>
                <span style={s.dropzoneText}>Trascina qui il PDF, o clicca per scegliere il file</span>
                <span style={s.dropzoneHint}>Solo file .pdf</span>
              </>
            )}
            <input type="file" accept="application/pdf" onChange={handleFileChange} style={{ display: 'none' }} />
          </label>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Incolla qui il testo della mail con l'ordine..."
            style={s.textarea}
            rows={10}
          />
        )}

        <div style={s.footer}>
          <button className="btn" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? 'Creazione...' : 'Crea ordine'}
          </button>
        </div>
      </div>
    </div>
  )
}

const s = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,10,10,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 480,
    background: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 20px 60px rgba(10,10,10,0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: 'var(--text)',
    margin: 0,
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-3)',
    borderRadius: 8,
    cursor: 'pointer',
  },
  subtitle: {
    fontSize: 12.5,
    color: 'var(--text-2)',
    lineHeight: 1.5,
    margin: 0,
    marginTop: -6,
  },
  tabs: {
    display: 'flex',
    gap: 6,
    padding: 4,
    background: 'var(--bg-subtle)',
    border: '1px solid var(--hairline)',
    borderRadius: 10,
  },
  tab: {
    flex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '8px 10px',
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'var(--text-2)',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
  },
  tabActive: {
    background: '#FFFFFF',
    color: 'var(--jenia-violet)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '32px 16px',
    border: '1.5px dashed var(--hairline-strong)',
    borderRadius: 12,
    background: 'var(--bg-subtle)',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'border-color 120ms ease, background 120ms ease',
  },
  dropzoneActive: {
    borderColor: 'var(--jenia-violet)',
    background: 'var(--grad-jenia-soft)',
  },
  dropzoneText: {
    fontSize: 12.5,
    color: 'var(--text-2)',
  },
  dropzoneHint: {
    fontSize: 11,
    color: 'var(--text-3)',
  },
  fileName: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
  },
  textarea: {
    padding: '10px 12px',
    fontSize: 13,
    border: '1px solid var(--hairline)',
    borderRadius: 10,
    background: 'var(--bg)',
    color: 'var(--text)',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: 160,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 4,
  },
}
